<template>
  <div class="widget w-order-status-widget" :class="['font-size-' + fontSize, { bold: isFontBold }]">
    <c-widget-header
      csvExport
      optimizeColumns
      fieldsConfigManager="advanced"
      hideLinkModeReceive
      printView
      showTDFields
      :wid="wid"
      @csv-export="onCsvExport"
      @optimize-columns="autoSizeColumns"
    >
      <slot name="buttons" slot="buttons" />
    </c-widget-header>

    <div v-if="connectionBroken" class="no-data-splash">
      <h3>{{ i18n('connectionBrokenHistoricalOrders') }}</h3>
    </div>
    <div class="widget-body" v-if="!connectionBroken">
      <c-widget-control-bar>
        <c-widget-account-select
          @accountChange="onAccountChange"
          :selectedAccountId="selectedAccountId ? selectedAccountId : defaultAccountStringID"
          :label="$t('white-label.widgets.accountDetails.headerLabels.account')"
          :label-cols="null"
          :allAccountsOption="allAccountsOption"
          widgetType="orderStatus"
          eligible
        />
        <c-widget-input-search
          :label="$t('white-label.globalLabels.symbol')"
          @input="onSymbolChange"
          @searchOrderDetails="searchOrderDetails"
          :value="symbolSearch"
          :powerUser="powerUser"
        />
        <c-widget-input-switch
          :label="$t(`${i18nNamespace}.accountHeader.orderDetails`)"
          :value="orderDetails"
          :options="this.toggleSwitchOptions()"
          @input="onOrderDetails"
        />
        <c-historical-orders-date-range-filter
          @onDateRangeChanged="onDateRangeChanged"
          @onFromDateChanged="onFromDateChanged"
          @onToDateChanged="onToDateChanged"
          @applyDateRange="applyDateRange"
          :selectedDateRangeId="selectedDateRangeId"
          :label="$t(`${i18nNamespace}.accountHeader.dateRange`)"
          :powerUser="powerUser"
          :fromDate="fromDate"
          :toDate="toDate"
          :maxDateRange="maxDateRange()"
        ></c-historical-orders-date-range-filter>
        <div class="form-group">
          <v-form-group :label="$t('white-label.globalLabels.template')">
            <v-form-select
              :options="fieldsConfigNameOptions"
              v-model="controlFieldsConfigName"
              :aria-label="
                $t('white-label.globalLabels.template') + ' ' + $t('white-label.globalLabels.screenReaderTemplate')
              "
            />
          </v-form-group>
        </div>
      </c-widget-control-bar>

      <c-filter-tabs
        class="mt-2 mb-2"
        :tabs="historicalOrdersTabs"
        @selected="onHistoricalOrderTabsSelected"
      ></c-filter-tabs>
      <div class="widget-body">
        <c-loader v-if="isLoading" />
        <ag-grid-vue
          v-if="isUserTypeAvailable"
          v-show="!isLoading"
          class="c-data-grid ag-theme-balham-dark"
          animateRows
          :asyncTransactionWaitMillis="gridUpdateInterval"
          suppressContextMenu
          :gridOptions="gridOptions"
          :suppressFieldDotNotation="true"
          :defaultColDef="defaultColDef"
          :rowData="orders"
          :columnDefs="columnDefs"
          :components="renderers"
          :groupDisplayType="'groupRows'"
          :groupSuppressRow="true"
          :suppressRowTransform="true"
          rowSelection="multiple"
          :rowHeight="agGridRowHeight"
          masterDetail="true"
          :detailCellRenderer="detailCellRenderer"
          :context="this"
          :detailRowHeight="0"
          ensureDomOrder
          @gridSizeChanged="onGridSizeChanged"
          :loadingCellRenderer="loadingCellRenderer"
          :noRowsOverlayComponent="noRowsOverlayRenderer"
          suppressDragLeaveHidesColumns
          :suppressColumnVirtualisation="isAccessibilityEnabled"
          @column-moved="onColMoved"
          @column-resized="onColResized"
          @drag-stopped="onGridDragEnd"
          @grid-ready="onGridReady"
          @selection-changed="onRowSelectionChanged"
          @sort-changed="sortChanged"
          @cell-focused="onCellFocused"
          @new-columns-loaded="ev => setColumnState(ev.columnApi)"
          @async-transactions-flushed="onAsyncTransactionsFlushed"
        />
      </div>
    </div>
  </div>
</template>

<script>
import CWidgetHeader from '@custom/widgetContainer/CWidgetHeader';
import CWidgetControlBar from '@custom/widgetContainer/CWidgetControlBar';
import CWidgetAccountSelect from '@custom/widgetContainer/CWidgetAccountSelect';
import CHistoricalOrdersDateRangeFilter from '@custom/CHistoricalOrdersDateRangeFilter';
import CWidgetInputSearch from '@custom/widgetContainer/CWidgetInputSearch';
import CWidgetInputSwitch from '@custom/widgetContainer/CWidgetInputSwitch';
import CFilterTabs from '@custom/CFilterTabs';
import CLoader from '@custom/CLoader';
import moment from 'moment-timezone';
import accountDetailsCommons from '@mixins/accountDetailsCommons.js';
import { rowHeightM } from '@mm5-components/scss/variables.scss';
import { ModificationButtonsRenderer } from '@custom/dataGrid/renderers';
import { FlagRenderer } from '@custom/dataGrid/renderers';
import { TickerRenderer } from '@custom/dataGrid/renderers';
import { QuoteRenderer } from '@custom/dataGrid/renderers';
import { GenericMLOCellRenderer } from '@custom/dataGrid/renderers';
import { OrderDetail } from '@custom/dataGrid/renderers';
import { LoadingCellRenderer } from '@custom/dataGrid/renderers';
import { AgGridVue } from 'ag-grid-vue';
import { NoRowsOverlayRenderer } from '@custom/dataGrid/renderers';
import { mapGetters } from 'vuex';

export default {
  name: 'w-historical-order-widget',
  mixins: [accountDetailsCommons],
  components: {
    CWidgetHeader,
    CWidgetControlBar,
    CWidgetAccountSelect,
    CWidgetInputSearch,
    CWidgetInputSwitch,
    CHistoricalOrdersDateRangeFilter,
    CFilterTabs,
    AgGridVue,
    FlagRenderer,
    TickerRenderer,
    QuoteRenderer,
    GenericMLOCellRenderer,
    CLoader,
    OrderDetail,
    LoadingCellRenderer,
    ModificationButtonsRenderer,
    NoRowsOverlayRenderer
  },
  props: {
    wid: {
      type: String,
      required: true
    },
    dateFormatForTDAPI: {
      type: String,
      required: false,
      default: 'YYYY-MM-DD'
    }
  },
  data() {
    return {
      regularUserCache: {
        ALL: null,
        FILLED: null,
        CANCELLED: null,
        EXPIRED: null
      },
      orders: [],
      rics: [],
      ricsArray: [],
      dateRangeValues: [1, 3, 7, 30, 60, -1, 1200],
      quoteFids: ['q.LOT_SIZE_A', 'q.RECORDTYPE', 'q._RBSS2004', 'q.TRD_STATUS'],
      orderSubmissionDateFormat: 'MM/DD/YYYY hh:mm a',
      dateAndTimeFormat: 'MM/DD/YYYY hh:mm:ss A',
      oddRow: true, //to keep track odd-even row calculation
      isHistorical: true,
      gridApi: null,
      detailCellRenderer: 'OrderDetail',
      loadingCellRenderer: 'LoadingCellRenderer',
      noRowsOverlayRenderer: 'NoRowsOverlayRenderer',
      isLoading: false,
      isActiveOrder: false,
      i18nNamespace: 'white-label.widgets.orderStatus',
      historicalOrdersTabs: [
        { name: 'filled', value: 'FILLED', pressed: false },
        { name: 'cancelled', value: 'CANCELLED', pressed: false },
        { name: 'expired', value: 'EXPIRED', pressed: false },
        { name: 'all', value: 'ALL', pressed: true }
      ],
      columns: [
        {
          valueFormatter: params => {
            if (params.data && params.data.security) return params.data.security.displayTicker;
            return '';
          },
          field: `ticker`,
          width: 120,
          cellClass: ['text-left', 'ticker-cell', 'actionable-cell'],
          cellRenderer: 'TickerRenderer',
          cellRendererParams: params => {
            return { orderDetail: this.orderDetails, tradeHaltValue: params.data['q.TRD_STATUS'] };
          },
          cellStyle: this.getSpanCellStyle,
          tooltipValueGetter: params => {
            if (params.data) {
              let toolTipValue = params.data['q._DSPLY_NAME'];
              if (this.isValidTradingHaltValue(params.data['q.TRD_STATUS'])) {
                toolTipValue = `${toolTipValue} ( ${this.i18n(
                  params.data['q.TRD_STATUS'].trim(),
                  'white-label.globalLabels.tradingHalt'
                )} )`;
              }
              return toolTipValue;
            } else return '';
          },
          type: ['actionable', 'mlo']
        },
        {
          valueFormatter: params => {
            if (params.data) return params.data.accountNumber;
            return '';
          },
          field: 'accountNumber',
          width: 120,
          cellClass: 'text-right'
        },
        {
          valueFormatter: params => {
            const country = params.data.security ? params.data.security.market : undefined;
            return country === 'CA' || country === 'US' ? country : '';
          },
          field: `country`,
          cellClass: 'text-center',
          cellRenderer: 'FlagRenderer'
        },
        {
          valueFormatter: params => {
            return this.$t(
              'white-label.globalLabels.' + this.getBuyOrSell(params.data.extendedTerms.orderAction).toLowerCase()
            ).toUpperCase();
          },
          field: `action`,
          cellClass: 'text-center',
          cellClassRules: {
            positive: params => params.data.extendedTerms.orderAction.startsWith('BUY'),
            negative: params => params.data.extendedTerms.orderAction.startsWith('SELL')
          }
        },
        {
          field: `quantity`,
          valueFormatter: params => {
            if (params.data.security.type === 'MUTUALFUND') return '';
            else {
              let quantity = this.$f(params.value, 'float.fractionalQuantity');
              if (params.data.dollarAmt === params.value) {
                quantity = '$' + quantity;
              }
              return quantity;
            }
          }
        },
        {
          valueGetter: params => {
            return this.getPriceInfo(params.data);
          },
          field: `priceInfo`,
          cellRenderer: 'GenericMLOCellRenderer',
          rowSpan: params => (params.data.strategy === 'MLO' ? this.getRowsToSpan(params) : 1),
          cellStyle: params => (params.data.strategy === 'MLO' ? this.getSpanCellStyle(params) : {})
        },
        {
          valueFormatter: params => {
            return this.getDuration(params.data);
          },
          field: `duration`,
          width: 120,
          cellStyle: this.getSpanCellStyle,
          cellRenderer: 'GenericMLOCellRenderer',
          cellClass: 'text-right',
          rowSpan: this.getRowsToSpan
        },
        {
          field: `status`,
          width: 120,
          cellStyle: params => (params.data.strategy === 'MLO' ? this.getSpanCellStyle(params) : {}),
          rowSpan: params => (params.data.strategy === 'MLO' ? this.getRowsToSpan(params) : 1),
          valueFormatter: params => this.getStatus(params.data),
          cellRenderer: this.getStatusCellRenderer,
          comparator: (value1, value2, node1, node2, isInverted) => {
            return this.sortComparator(value1.displayStatus, value2.displayStatus, node1, node2, isInverted);
          }
        },
        {
          rowSpan: params => (params.data.strategy === 'MLO' ? this.getRowsToSpan(params) : 1),
          cellStyle: params => (params.data.strategy === 'MLO' ? this.getSpanCellStyle(params) : {}),
          cellRenderer: 'GenericMLOCellRenderer',
          field: `avgFillPrice`
        },
        {
          valueFormatter: params => {
            if (params.data.security.type === 'MUTUALFUND') return '';
            else {
              let remainingQty = this.$f(params.value, 'float.fractionalQuantity');
              if (params.data.extendedTerms.remainingDollarAmt === params.value) {
                remainingQty = '$' + remainingQty;
              }
              return remainingQty;
            }
          },
          field: `remainingQty`
        },
        {
          field: `modifications`,
          cellClass: ['text-left', 'modification-buttons-cell'],
          cellClassRules: {
            'actionable-cell': params => !this.isModificationCellActionable(params)
          },
          width: 190,
          rowSpan: this.getMCRowsToSpan,
          cellStyle: this.getMCSpanCellStyle,
          cellRenderer: 'ModificationButtonsRenderer',
          cellRendererParams: params => {
            return { modificationStrArr: this.getModification(params), wid: this.wid };
          },
          sortable: false,
          suppressKeyboardEvent: params => {
            const key = params.event.code || params.event.key;
            return key === 'Tab';
          },
          type: ['actionable', 'mlo']
        },
        {
          field: `dateTime`,
          width: 140,
          cellRenderer: 'GenericMLOCellRenderer',
          cellStyle: this.getSpanCellStyle,
          cellClass: 'text-center',
          rowSpan: this.getRowsToSpan
        },
        {
          valueFormatter: params => {
            if (params.data.orderDate && moment(params.data.orderDate).isValid())
              return moment(params.data.orderDate)
                .tz(this.computedTZ)
                .format(this.dateAndTimeFormat);
          },
          field: `orderDate`,
          width: 140,
          rowSpan: this.getRowsToSpan,
          cellStyle: this.getSpanCellStyle,
          cellRenderer: 'GenericMLOCellRenderer',
          cellClass: 'text-center'
        }
      ],
      gridOptions: {
        headerHeight: +rowHeightM,
        rowHeight: +rowHeightM,
        isExternalFilterPresent: () => true,
        doesExternalFilterPass: this.applySymbolFilterForRow,
        getRowClass: this.getRowClass,
        rowClassRules: {
          'row-option-expired': params => {
            if (params.data) {
              if (params.data.security.type === 'OPTION') {
                const expiryDate = params.data.security.tickerElements.contractExpiry;
                return moment(expiryDate, 'DDMMMYY').isBefore(moment(), 'd');
              }
            }
            return false;
          }
        },
        isRowMaster: dataItem => {
          return !dataItem.root || dataItem.strategy === 'SPL';
        },
        getRowNodeId: data => {
          return data.id;
        },
        rowModelType: 'clientSide',
        columnTypes: { actionable: {}, mlo: {} },
        navigateToNextCell: this.navigateToNextCell,
        tabToNextHeader: this.tabToNextHeader,
        tabToNextCell: this.tabToNextCell
      },
      renderers: [FlagRenderer, TickerRenderer],
      accountNumbersList: []
    };
  },
  computed: {
    ...mapGetters({
      accounts: 'accounts/getOrderStatusAccountData',
      oeAccounts: 'accounts/getOrderEntryAccountData',
      config: 'accounts/getConfigData',
      powerUser: 'accounts/getHistoricalOrdersPowerUserFlag',
      allAccountsOption: 'accounts/isAllAccountsAvailableForHistoricalOrders',
      isUserTypeAvailable: 'accounts/getHistoricalOrdersCountAvailabilityFlag'
    }),
    connectionBroken() {
      return this._.isEmpty(this.config) || this._.isEmpty(this.accounts);
    },
    selectedAccountId() {
      return this.matchedAccount
        ? this.matchedAccount.id
        : this.allAccountsOption
        ? 'allAccounts'
        : this.matchedAccount;
    },
    matchedAccount() {
      return this.accounts.find(account => account.id === this.$store.state.widget[this.wid].selectedAccountId);
    },
    defaultAccountStringID() {
      let defaultAccount = this.accounts.find(account => account.number === this.defaultAccountId);
      return defaultAccount ? defaultAccount.id : defaultAccount;
    },
    statusCategoryInd() {
      return this.$store.state.widget[this.wid].statusCategoryIndex;
    },
    symbolSearch() {
      return this.$store.state.widget[this.wid].symbolSearch;
    },
    orderDetails() {
      return this.$store.state.widget[this.wid].orderDetails;
    },
    selectedDateRangeId() {
      return this.$store.state.widget[this.wid].selectedDateRangeId;
    },
    fromDate() {
      return this.$store.state.widget[this.wid].fromDate;
    },
    toDate() {
      return this.$store.state.widget[this.wid].toDate;
    },
    loggedIn() {
      return this.$store.getters['auth/loggedIn'];
    },
    powerUserCacheBlockSize() {
      return this.$store.state.accounts.powerUserPageSize;
    },
    computedTZ() {
      return this.$store.state.applicationSettings.general.settings.timezone;
    }
  },
  mounted() {
    this.historicalOrdersTabs.map((item, idx) => {
      item.pressed = false;
      if (idx === this.statusCategoryInd) {
        item.pressed = true;
      }
    });
    this.accountNumbersList = this.oeAccounts.map(accountObj => accountObj.number);
  },
  beforeDestroy() {
    this.streamingUnsubscribe();
    if (this.gridOptions) this.gridOptions = null;
    if (this.gridApi) this.gridApi = null;
  },
  methods: {
    getParamsForPowerUser(startRow, endRow, sortModel) {
      let params = {};
      if (this.selectedAccountId !== 'allAccounts') {
        params['accountId'] = this.selectedAccountId;
      }
      if (this.statusCategoryInd !== 3) {
        params['status'] = this.historicalOrdersTabs[this.statusCategoryInd].value;
      }
      if (this.symbolSearch !== '') {
        params['symbol'] = this.symbolSearch;
      }
      if (this.selectedDateRangeId !== 5) {
        params['toDate'] = moment()
          .subtract(1, 'day')
          .format('YYYY-MM-DD');
        params['fromDate'] = moment()
          .subtract(this.dateRangeValues[this.selectedDateRangeId], 'day')
          .format('YYYY-MM-DD');
      } else {
        params['toDate'] = moment(this.toDate).format(this.dateFormatForTDAPI);
        params['fromDate'] = moment(this.fromDate).format(this.dateFormatForTDAPI);
      }
      if (sortModel && sortModel.length) {
        const col = sortModel[0];
        if (this.sortFieldMap.hasOwnProperty(col.colId)) {
          const sortField = this.sortFieldMap[col.colId];
          const sortParam = `${sortField},${col.sort === 'desc' ? 'DES' : 'ASC'}`; //Array of values with same key requires paramsSerialization in axios
          params['orderBy'] = sortParam;
        }
      }
      let pageNo = Math.floor(startRow / (endRow - startRow)) + 1;
      params['page'] = pageNo;
      params['pageSize'] = endRow - startRow;
      return params;
    },
    onHistoricalOrderTabsSelected(tab) {
      this.historicalOrdersTabs.forEach((item, idx) => {
        item.pressed = false;
        if (idx === tab.index) {
          item.pressed = true;
        }
      });
      this.$store.commit(`widget/${this.wid}/UPDATE_STATUS_CATEGORY_IND`, tab.index);
      if (this.powerUser) {
        this.triggerServerQuery();
      } else {
        this.applyLocalFilters();
      }
    },
    onAccountChange(account) {
      this.$store.commit(`widget/${this.wid}/UPDATE_ACCOUNTID`, account.id);
      if (this.powerUser) {
        this.triggerServerQuery();
      } else {
        this.reloadOrders();
      }
    },
    reloadOrders() {
      //invalidate cache
      Object.keys(this.regularUserCache).forEach(filterKey => (this.regularUserCache[filterKey] = null));

      this.isLoading = true;
      this.streamingUnsubscribe();

      return this.getHistoricalOrders()
        .then(({ orders }) => this.onFetchSuccess(orders))
        .finally(() => {
          this.isLoading = false;
        })
        .catch(err => {
          this.orders = []; //set error
          console.error(err);
        });
    },
    //Apply all the filters on the bucketed and cached orders
    applyLocalFilters() {
      if (this.powerUser) return;
      if (this.selectedDateRangeId === 5 && (!this.fromDate || !this.toDate)) return;

      const tab = this.historicalOrdersTabs[this.statusCategoryInd];
      let orders = this.regularUserCache[tab.value];
      if (!orders) return;

      if (orders.length) orders = this.filterByDateRange(orders);
      this.orders = orders;
    },
    onSymbolChange(text) {
      this.$store.commit(`widget/${this.wid}/UPDATE_SYMBOLSEARCH`, text);
    },
    onDateRangeChanged($event) {
      this.$store.commit(`widget/${this.wid}/UPDATE_DATE_RANGE`, $event);
      if (!this.powerUser) this.applyLocalFilters();
      else {
        if (this.selectedDateRangeId === 5 && (!this.fromDate || !this.toDate)) return;
        this.triggerServerQuery();
      }
    },
    onFromDateChanged($event) {
      this.$store.commit(`widget/${this.wid}/UPDATE_FROM_DATE`, $event);
      //Non-Power users date change reflected instantaneously
      if (!this.powerUser && this.selectedDateRangeId === 5 && this.toDate) this.applyLocalFilters();
    },
    onToDateChanged($event) {
      this.$store.commit(`widget/${this.wid}/UPDATE_TO_DATE`, $event);
      //Non-Power users date change reflected instantaneously
      if (!this.powerUser && this.selectedDateRangeId === 5 && this.fromDate) this.applyLocalFilters();
    },
    maxDateRange() {
      return Math.max(...this.dateRangeValues);
      //  return this.$store.getters[`orgSettings/getHistoricalOrdersMaxDate`];
    },
    onOrderDetails($event) {
      this.$store.commit(`widget/${this.wid}/UPDATE_ORDER_DETAILS`, $event);
      this.toggleAllNodes();
    },
    onGridReady(ev) {
      this.gridApi = ev.api;
      this.onTableReady(ev, this.i18n('historicalTitle'));
    },
    applyDateRange() {
      if (this.powerUser) {
        this.triggerServerQuery();
      }
    },
    fetch() {}, // dummy implementation for template change
    fetchRows(newFids, prevFids) {
      if (newFids) {
        const reload = newFids.some(newFid => {
          if (!newFid.startsWith('td.')) {
            return !prevFids.includes(newFid);
          }
          return false;
        });
        if (reload) {
          this.streamingUnsubscribe();
          const orders = this.getDisplayedNodes().map(node => node.data);
          this.initiateStreaming(orders);
        }
      }
    },
    onFetchSuccess(allOrders) {
      this.fillAllBuckets(allOrders);
      this.applyLocalFilters();
      this.initiateStreaming(allOrders);
    },
    fillAllBuckets(allOrders) {
      this.regularUserCache['ALL'] = allOrders;
      if (allOrders && allOrders.length) {
        this.historicalOrdersTabs.forEach((tab, i) => {
          if (tab.value !== 'ALL') {
            this.regularUserCache[tab.value] = this.createOrderStatusBuckets(allOrders, i);
          }
        });
      }
    },
    searchOrderDetails() {
      if (this.powerUser) this.triggerServerQuery();
    },
    filterByDateRange(orders) {
      const dateRangeArray = this.getValidDateRange();
      const processedStrategies = {}; //to avoid re-processing
      return orders.filter(order => {
        if (processedStrategies.hasOwnProperty(order.orderKey)) return processedStrategies[order.orderKey];

        //Filter the order if at least one of the legs/orders' timestamp in the strategy matches
        const valid = order.allOrderDates.some(orderDate => {
          if (orderDate) {
            return moment(moment(orderDate).format('YYYY-MM-DD')).isBetween(
              dateRangeArray['fromDate'],
              dateRangeArray['toDate']
            );
          }
          return false;
        });
        processedStrategies[order.orderKey] = valid;
        return valid;
      });
    },
    getPriceInfo(order) {
      let displayOrderType = this.getPriceTypeMapping(order);
      let limit = undefined,
        trigger = undefined;
      //limit price
      if (
        [
          'LIMIT',
          'STOP_LIMIT',
          'MARKET_TRIGGERED',
          'LIMIT_TRIGGERED',
          'TRAILING_STOP_LIMIT',
          'NET_DEBIT',
          'NET_CREDIT'
        ].includes(displayOrderType)
      ) {
        if (order.strategy === 'MLO') limit = order.multilegEntry.extendedTerms.limitPrice;
        else limit = order.extendedTerms.limitPrice;
      }
      //trigger price
      if (order.strategy !== 'MLO') {
        if (['STOP_MARKET', 'STOP_LIMIT', 'TRAILING_STOP_MARKET', 'TRAILING_STOP_LIMIT'].includes(displayOrderType)) {
          trigger = order.extendedTerms.triggerPrice;
        }
      }
      const format = (val, altText) => {
        if (val && !isNaN(val)) {
          return this.$f(val, 'float.priceTD');
        }
        return altText;
      };
      let priceInfo = this.i18n(`priceTypes.short.${displayOrderType}`);
      const altText = this.i18n('priceTypes.short.na');
      if (trigger) {
        priceInfo = `${priceInfo} ${format(trigger, altText)}`;
        if (limit) {
          priceInfo = `${priceInfo}/${format(limit, altText)}`;
        }
      } else if (limit) {
        priceInfo = `${priceInfo} ${format(limit, altText)}`;
      }
      return priceInfo;
    },
    invokeModificationAction(rowNode, actionType) {
      let allLeafChildrenData = this.getAllNodesByOrderKey(rowNode.data.orderKey);
      const ricArray = allLeafChildrenData.map(leafData =>
        leafData.data.security.type === 'EQUITY' ? leafData.data.underlyingRic : leafData.data['q.STOCK_RIC']
      ); //here we can directly us the stock ric instead of doing ricByTicker call
      this.triggerOrderEntryOpen(rowNode, actionType, allLeafChildrenData, ricArray);
    },
    triggerOrderEntryOpen(rowNode, actionType, allLeafChildrenData, ricArray) {
      let legObj = [];
      let strategy = {};
      let isMlo = false;
      let conditionalOrders = false;
      let conditionalOrderType = '';

      conditionalOrderType = rowNode.data.strategy.toLowerCase();
      let dataArrayForOrderEntry = [];

      if (rowNode.data.strategy === 'MLO') {
        const mloExtTerm = rowNode.data.multilegEntry.extendedTerms;
        const mloPricePlan = rowNode.data.pricePlan;
        let duration = '';
        let goodTill = null;

        if (mloExtTerm.goodTill === 'DATE') {
          duration = 'gtd';
          if (new Date(mloExtTerm.goodTillDate) < new Date() && (actionType === 'copy' || actionType === 'reverse')) {
            goodTill = new Date();
          } else {
            goodTill = new Date(mloExtTerm.goodTillDate);
          }
        } else if (mloExtTerm.goodTill === 'CANCEL') {
          duration = 'gtc';
        } else {
          duration = 'day';
        }

        isMlo = true;
        strategy = {
          type: rowNode.data.security.type === 'OPTION' ? 'customOptionOption' : 'customStockOption',
          priceType: mloPricePlan.includes('NET') ? 'limit' : mloPricePlan.toLowerCase(),
          limit: mloPricePlan.includes('NET') ? mloExtTerm.limitPrice : 0,
          netCreditDebit: mloPricePlan.includes('CREDIT') ? 'CR' : 'DR',
          duration,
          goodTill
        };
      }

      if (rowNode.data.allSameStatus) {
        if (allLeafChildrenData.length > 1 && !isMlo) {
          conditionalOrders = true;
        }
        dataArrayForOrderEntry = allLeafChildrenData;
      } else if (rowNode.data.ftoSecondLegSame && rowNode.data.index !== 0) {
        conditionalOrderType = 'oco';
        conditionalOrders = true;
        dataArrayForOrderEntry[0] = allLeafChildrenData[1];
        dataArrayForOrderEntry[1] = allLeafChildrenData[2];
      } else {
        dataArrayForOrderEntry[0] = rowNode;
      }

      const orderLeg = ['firstOrder', 'secondOrder', 'thirdOrder'];
      for (let iCtr = 0; iCtr < dataArrayForOrderEntry.length; iCtr++) {
        legObj[orderLeg[iCtr]] = this.populateLegValuesForOrderEntry(
          dataArrayForOrderEntry[iCtr].data,
          actionType,
          dataArrayForOrderEntry[iCtr].data.allTickerArray[iCtr],
          ricArray[iCtr]
        );
      }
      let config = {};
      if (window.opener !== null) {
        config.workspaceId = 'POPOUT';
      }
      config.accountId = rowNode.data.accountId;
      config.legs = legObj;
      if (isMlo) {
        config.strategy = strategy;
      }
      config.profitLossExits = false;
      if (conditionalOrders) {
        config.conditionalOrders = conditionalOrders;
        config.conditionalOrderType = conditionalOrderType;
      }
      config.profitTaker = false;
      config.profitStopLoss = false;
      if (actionType === 'modify') {
        config.orderMode = { action: 'MODIFY', modifier: '' };
        config.orderKeyEncoded = rowNode.data.orderKey;
      } else if (actionType === 'copy' || actionType === 'reverse') {
        config.orderMode = { action: 'COPYREVERSE', modifier: '' };
      }
      if (rowNode.data.isFractional) {
        config.legs['firstOrder'].fractionalQuantityType =
          rowNode.data.isNotional && config.legs['firstOrder'].action !== 'sell' ? '$' : '#';
        if (
          rowNode.data.isNotional &&
          rowNode.data.orderStatus === 'FILLED' &&
          config.legs['firstOrder'].action !== 'sell'
        ) {
          config.legs['firstOrder'].quantity = rowNode.data.dollarAmt;
        }
      }

      config.legs['firstOrder'].ticketType = rowNode.data.isFractional
        ? 'fractional'
        : config.legs['firstOrder'].ticketType;

      this.$widget.openOrderEntry(config);
    },
    invokeTabNextCell(value) {
      let isTab = !value.shiftKey && value.key === 'Tab';
      if (isTab) {
        value.preventDefault();
        this.gridOptions.api.tabToNextCell();
      }
    },
    invokeTabPreviousCell(value) {
      let shiftTab = value.shiftKey && value.key === 'Tab';
      if (shiftTab) {
        value.preventDefault();
        this.gridOptions.api.tabToPreviousCell();
      }
    }
  },
  watch: {
    symbolSearch: {
      //update in store trigger the filter
      immediate: true,
      handler() {
        if (this.gridOptions.api && !this.powerUser) {
          this.gridOptions.api.onFilterChanged();
        }
      }
    },
    loggedIn: {
      //This method should be replaced once we finalize on how do
      // we persist the state values after logout. For every new logins,
      // default account from config data should be the first one to be
      // displayed in account drop down. And we do not save the account id
      // in our backend due to seucirty reasons. Hence, clearing out this
      // value during logout.
      immediate: true,
      handler() {
        if (!this.loggedIn) {
          this.$store.commit(`widget/${this.wid}/UPDATE_ACCOUNTID`, '');
        }
      }
    },
    tableFids(newFids, prevFids) {
      this.fetchRows(newFids, prevFids);
    },
    isUserTypeAvailable: {
      immediate: true,
      handler(value) {
        if (value && this.powerUser) {
          this.gridOptions.rowModelType = 'serverSide';
          this.gridOptions.serverSideStoreType = 'partial';
          this.gridOptions.serverSideDatasource = this.getServerDataSource();
          this.gridOptions.cacheBlockSize = this.powerUserCacheBlockSize;
        }
      }
    }
  }
};
</script>

<style lang="scss">
@import '~@/styles/vendor/ag-grid';
@import '~@/styles/widgets/holdings-widget';
@import '~@/styles/widgets/order-status-widget';
</style>
